import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { LogOut, Calendar, Clock, DollarSign, CheckCircle, XCircle, Search } from 'lucide-react';

export default function StaffPortal() {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [appointments, setAppointments] = useState([]);

  // Check local storage for persistent login
  useEffect(() => {
    const savedStaff = localStorage.getItem('keyla_staff_session');
    if (savedStaff) {
      setStaffData(JSON.parse(savedStaff));
      setIsAuthenticated(true);
    }
  }, []);

  const fetchAppointments = async (staffName, businessId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('business_id', businessId)
        .eq('staffAssigned', staffName)
        .eq('date', today)
        .order('time', { ascending: true });
        
      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      console.error("Error fetching appointments:", err);
    }
  };

  useEffect(() => {
    if (isAuthenticated && staffData) {
      fetchAppointments(staffData.name, staffData.business_id);
    }
  }, [isAuthenticated, staffData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // 1. Validar el staff con el pin y un identificador (en este caso usamos el ID o un nombre si no hay teléfono guardado)
      // Como no guardaste explícitamente "phone" en la tabla staff, buscaríamos por algún cruce.
      // Si tienen 'id_whatsapp', se busca por ahí. Asumamos que buscamos por pin y verificamos el nombre o teléfono.
      
      // Teléfono Y PIN, comprobados en la base (con límite de intentos). La tabla de equipo ya no se lee desde el navegador: el PIN nunca viaja hacia acá.
      const { data, error } = await supabase.rpc('delivery_login', { p_phone: phone, p_pin: pin });
      if (data?.error === 'bloqueado') throw new Error("Demasiados intentos con un PIN equivocado. Espera 15 minutos.");
      if (error || !data?.id) throw new Error("PIN o teléfono incorrectos.");

      // La sesión guarda solo lo necesario (sin PIN).
      const user = { id: data.id, name: data.name, business_id: data.business_id, telefono: data.telefono, comision_porcentaje: Number(data.comision_porcentaje) || 0 };

      setStaffData(user);
      localStorage.setItem('keyla_staff_session', JSON.stringify(user));
      setIsAuthenticated(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('keyla_staff_session');
    setIsAuthenticated(false);
    setStaffData(null);
    setPhone('');
    setPin('');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col justify-center items-center p-4 font-sans text-slate-100">
        <div className="bg-slate-800 p-8 rounded-3xl shadow-2xl max-w-sm w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-seryi-green rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-seryi-green/20">
              <Calendar className="text-slate-900 w-8 h-8" />
            </div>
            <h1 className="font-black text-2xl tracking-tight">Portal Staff</h1>
            <p className="text-xs text-slate-400 mt-1 uppercase tracking-widest font-bold">Acceso Rápido</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-400 ml-1">Teléfono (WhatsApp)</label>
              <input 
                type="tel" 
                required
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-seryi-green transition-all mt-1"
                placeholder="3001234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-400 ml-1">PIN de 4 dígitos</label>
              <input 
                type="password" 
                required
                maxLength="4"
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-seryi-green transition-all mt-1 text-center tracking-[0.5em] font-bold"
                placeholder="••••"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
              />
            </div>

            {error && <p className="text-rose-500 text-xs font-bold text-center bg-rose-500/10 py-2 rounded-lg">{error}</p>}

            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-seryi-green text-slate-900 hover:bg-seryi-green/90 disabled:opacity-50 font-bold py-3 px-4 rounded-xl text-sm transition-all shadow-lg shadow-seryi-green/20 mt-2"
            >
              {loading ? "Verificando..." : "Ingresar a mi Agenda"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard del Staff
  // Para las comisiones, necesitamos el precio del servicio de cada cita, que podrías guardar en la cita o consultar en services.
  // Por simplicidad, si asumes precio en la cita:
  const dailyTotal = appointments
    .filter(a => a.status === 'completada' || a.status === 'pagada')
    .reduce((sum, a) => sum + ((a.price || 0) * ((staffData.commission_percentage || staffData.comision_porcentaje || 0) / 100)), 0);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24">
      {/* Header */}
      <header className="bg-slate-900 text-slate-100 p-4 sticky top-0 z-10 shadow-lg">
        <div className="max-w-md mx-auto flex justify-between items-center">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-seryi-green font-bold">{staffData?.businesses?.nombre_negocio || 'Bienvenido'}</p>
            <h1 className="font-bold text-lg">{staffData?.name}</h1>
          </div>
          <button onClick={handleLogout} className="bg-slate-800 p-2 rounded-lg text-slate-400 hover:text-white transition-colors">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 mt-2 space-y-6">
        
        {/* Comisiones Widget */}
        <section className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Comisiones Hoy</p>
            <h2 className="text-3xl font-black text-slate-900 mt-1">${dailyTotal.toLocaleString()}</h2>
            <p className="text-[10px] text-emerald-600 font-bold bg-emerald-50 inline-block px-2 py-0.5 rounded-full mt-1">
              Comisión: {staffData?.commission_percentage || staffData?.comision_porcentaje || 0}%
            </p>
          </div>
          <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center">
            <DollarSign className="text-slate-400" />
          </div>
        </section>

        {/* Timeline */}
        <section>
          <div className="flex justify-between items-end mb-4">
            <h2 className="font-bold text-slate-900 text-lg">Mi Agenda Hoy</h2>
            <button className="text-xs font-bold text-seryi-green bg-slate-900 px-3 py-1.5 rounded-lg shadow-md">
              + Bloquear Horario
            </button>
          </div>
          
          <div className="space-y-3">
            {appointments.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-2xl border border-slate-100 border-dashed">
                <Search className="mx-auto text-slate-300 mb-2" size={32} />
                <p className="text-slate-500 font-medium">No tienes citas agendadas para hoy.</p>
              </div>
            ) : appointments.map(appt => (
              <div key={appt.id} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-4">
                <div className="flex flex-col items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-slate-500">{appt.time.split(' ')[0]}</span>
                  <span className="text-[10px] text-slate-400">{appt.time.split(' ')[1]}</span>
                </div>
                
                <div className="w-px bg-slate-100 shrink-0 relative">
                  <div className={`absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 rounded-full ${appt.status === 'completada' || appt.status === 'pagada' ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 text-sm">{appt.client}</h3>
                  <p className="text-xs text-slate-500">{appt.service}</p>
                  
                  <div className="mt-2 flex justify-between items-center">
                    <span className="text-[10px] font-bold text-slate-400">{appt.phone}</span>
                    {appt.status === 'completada' || appt.status === 'pagada' ? (
                      <span className="text-[10px] uppercase font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md flex items-center gap-1">
                        <CheckCircle size={10} /> Pagado
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-md flex items-center gap-1">
                        <Clock size={10} /> Confirmada
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

      </main>
    </div>
  );
}
